/**
 * Presentation for money, quantities and rates.
 *
 * Every function here formats from the exact representation and never routes the value
 * through `Number`. `Intl.NumberFormat` is used only for the parts that cannot go wrong:
 * grouping an integer (it accepts a `bigint` directly) and telling us what a locale's
 * separators and currency affixes actually are.
 *
 * `Intl.NumberFormat(...).format(someNumber)` on a paise total is exactly the bug this
 * module exists to prevent, so it appears nowhere below.
 */
import { Dec } from './decimal.js';
import { fromPaise } from './money.js';
const affixCache = new Map();
const groupingCache = new Map();
const separatorCache = new Map();
/** What this locale puts between the whole part and the fraction. Cached; Intl is slow. */
function decimalSeparatorFor(locale) {
    const hit = separatorCache.get(locale);
    if (hit !== undefined)
        return hit;
    const found = new Intl.NumberFormat(locale, { style: 'decimal', minimumFractionDigits: 1 })
        .formatToParts(1.1)
        .find((p) => p.type === 'decimal')?.value ?? '.';
    separatorCache.set(locale, found);
    return found;
}
function affixesFor(locale, currency) {
    const cacheKey = `${locale}|${currency}`;
    const hit = affixCache.get(cacheKey);
    if (hit)
        return hit;
    let resolved = { prefix: '', suffix: '', decimalSeparator: '.' };
    try {
        const parts = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).formatToParts(1);
        const firstDigit = parts.findIndex((p) => p.type === 'integer');
        const fraction = parts.findIndex((p) => p.type === 'fraction');
        const decimal = parts.find((p) => p.type === 'decimal');
        resolved = {
            prefix: firstDigit < 0 ? '' : parts.slice(0, firstDigit).map((p) => p.value).join(''),
            suffix: fraction < 0 ? '' : parts.slice(fraction + 1).map((p) => p.value).join(''),
            decimalSeparator: decimal?.value ?? '.',
        };
    }
    catch {
        // An unknown currency code should degrade to plain digits, not throw inside a render.
        resolved = { prefix: `${currency} `, suffix: '', decimalSeparator: '.' };
    }
    affixCache.set(cacheKey, resolved);
    return resolved;
}
function groupInteger(value, locale) {
    let formatter = groupingCache.get(locale);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, { style: 'decimal', useGrouping: true });
        groupingCache.set(locale, formatter);
    }
    // Intl.NumberFormat accepts a bigint and formats every digit of it. This is the only
    // safe way to group an amount that may exceed Number.MAX_SAFE_INTEGER.
    return formatter.format(value);
}
/**
 * Format an integer-paise amount for display.
 *
 * `formatPaise(123456789n as Paise)` -> `Rs 12,34,567.89` under `en-IN`.
 */
export function formatPaise(amount, options = {}) {
    const { locale = 'en-IN', currency = 'INR', omitSymbol = false, zeroAs } = options;
    if (amount === 0n && zeroAs !== undefined)
        return zeroAs;
    const { prefix, suffix, decimalSeparator } = affixesFor(locale, currency);
    const negative = amount < 0n;
    const magnitude = negative ? -amount : amount;
    const rupees = magnitude / 100n;
    const fraction = (magnitude % 100n).toString().padStart(2, '0');
    const body = `${groupInteger(rupees, locale)}${decimalSeparator}${fraction}`;
    const withAffixes = omitSymbol ? body : `${prefix}${body}${suffix}`;
    return negative ? `-${withAffixes}` : withAffixes;
}
/** The same amount as plain exact digits, e.g. for a CSV cell or a `value` attribute. */
export function paiseToPlainString(amount) {
    return fromPaise(amount).toFixed(2);
}
/**
 * Format a quantity or any `Numeric(20, 4)` value.
 *
 * Grouping is applied to the integer part only; the fraction is emitted digit for digit
 * from the exact representation.
 */
export function formatDecimal(value, options = {}) {
    const { locale = 'en-IN', places = 4, trim = true, minPlaces = 0, unit, signed = false, } = options;
    let rounded = Dec.parse(value).round(places);
    if (trim) {
        const trimmed = rounded.trim();
        rounded = trimmed.scale < minPlaces ? trimmed.round(minPlaces) : trimmed;
    }
    const negative = rounded.isNegative;
    const magnitude = rounded.abs();
    const digits = magnitude.toString();
    const dot = digits.indexOf('.');
    const wholeText = dot < 0 ? digits : digits.slice(0, dot);
    const fractionText = dot < 0 ? '' : digits.slice(dot + 1);
    // A locale whose decimal separator is not "." must not be given a "." to print.
    const separator = decimalSeparatorFor(locale);
    const grouped = groupInteger(BigInt(wholeText), locale);
    const body = fractionText ? `${grouped}${separator}${fractionText}` : grouped;
    const sign = negative ? '-' : signed && !rounded.isZero ? '+' : '';
    return unit ? `${sign}${body} ${unit}` : `${sign}${body}`;
}
/** A per-unit rate. Same machinery, but pinned at 4 decimals and not trimmed by default. */
export function formatRate(value, options = {}) {
    const { locale = 'en-IN', currency = 'INR', omitSymbol = false } = options;
    const { prefix, suffix } = affixesFor(locale, currency);
    const body = formatDecimal(value, { locale, places: 4, trim: true, minPlaces: 2 });
    if (omitSymbol)
        return body;
    const negative = body.startsWith('-');
    const magnitude = negative ? body.slice(1) : body;
    return `${negative ? '-' : ''}${prefix}${magnitude}${suffix}`;
}
/**
 * A quantity rendered at its UOM's own precision.
 *
 * `uoms.decimal_places` exists on the server precisely so a UI does not show `12.0000 NOS`
 * for a unit that cannot be subdivided.
 */
export function formatQuantity(value, uom, options = {}) {
    const opts = {
        places: uom?.decimal_places ?? 4,
        ...options,
    };
    if (uom?.code !== undefined && options.unit === undefined)
        opts.unit = uom.code;
    return formatDecimal(value, opts);
}
//# sourceMappingURL=format.js.map